import { zodResolver } from '@hookform/resolvers/zod';
import { listLocations } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import {
  type CreateEventRequest,
  CreateEventRequestSchema,
  type EventStatus,
  type LocationResponse,
  UpdateEventRequestSchema,
} from '@jq4race/shared';
import { Button, Input, Label } from '@jq4race/ui';
import { TriangleAlert } from 'lucide-react';
import { forwardRef, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { CHARGE_MODE_OPTIONS, EVENT_PHOTO_POLICY_OPTIONS, isFieldEditable } from '../constants';

// ── Types ──────────────────────────────────────────────────────────────────

interface EventFormProps {
  mode: 'create' | 'edit';
  onSubmit: (values: CreateEventRequest) => Promise<void>;
  defaultValues?: Partial<CreateEventRequest>;
  /** Only present in edit mode — drives the R1.4 field-editability matrix. */
  status?: EventStatus;
  submitLabel?: string;
  onCancel?: () => void;
}

// ── Component ──────────────────────────────────────────────────────────────

export function EventForm({
  mode,
  onSubmit,
  defaultValues,
  status,
  submitLabel,
  onCancel,
}: EventFormProps) {
  const { t } = useTranslation('events');
  const schema = mode === 'create' ? CreateEventRequestSchema : UpdateEventRequestSchema;
  const [serverError, setServerError] = useState<string | null>(null);
  const [locations, setLocations] = useState<LocationResponse[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(true);

  const effectiveStatus: EventStatus = status ?? 'draft';
  const editable = {
    name: isFieldEditable(effectiveStatus, 'name'),
    locationId: isFieldEditable(effectiveStatus, 'locationId'),
    scheduledDate: isFieldEditable(effectiveStatus, 'scheduledDate'),
    scheduledTime: isFieldEditable(effectiveStatus, 'scheduledTime'),
    expectedCapacity: isFieldEditable(effectiveStatus, 'expectedCapacity'),
    chargeMode: isFieldEditable(effectiveStatus, 'chargeMode'),
    photoPolicy: isFieldEditable(effectiveStatus, 'photoPolicy'),
  };
  const isFrozen =
    mode === 'edit' && (effectiveStatus === 'active' || effectiveStatus === 'closed');

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CreateEventRequest>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      locationId: '',
      scheduledDate: '',
      scheduledTime: null,
      // `undefined`, not `null`, on purpose: react-hook-form's uncontrolled
      // sync-on-mount for a `setValueAs`-registered numeric field re-derives
      // the value from the (empty) DOM ref once it already holds a non-undefined
      // default, which turns an explicit `null` default into `0` and trips the
      // schema's min(1) check. `undefined` skips that re-derivation and the
      // field resolves to `null` correctly via setValueAs('') once the user
      // interacts with it (or leaves it untouched). Schema-wise this is a
      // no-op: expectedCapacity is `.nullable().optional()`.
      expectedCapacity: undefined,
      chargeMode: 'per_attendee',
      photoPolicy: 'deferred',
      ...defaultValues,
      ...(defaultValues?.expectedCapacity == null ? { expectedCapacity: undefined } : {}),
    },
  });

  useEffect(() => {
    // The frozen (active/closed) view never renders the location select — skip
    // the fetch entirely to avoid an unnecessary request and a state update
    // on an unmounted-in-spirit branch of the component.
    if (isFrozen) return;
    let cancelled = false;
    async function loadLocations() {
      setLocationsLoading(true);
      try {
        const res = await listLocations({ status: 'active' });
        if (!cancelled) setLocations(res.locations);
      } finally {
        if (!cancelled) setLocationsLoading(false);
      }
    }
    void loadLocations();
    return () => {
      cancelled = true;
    };
  }, [isFrozen]);

  useEffect(() => {
    // The Location <select> is uncontrolled: `register` only pushes its
    // `defaultValue` onto the DOM node once, at mount. In edit mode the
    // locations list is still loading at that point, so the saved
    // `locationId` doesn't match any `<option>` yet and the browser silently
    // drops it — the select renders as "Select a location" even though a
    // value is set in form state. Once loading finishes (and the matching
    // `<option>` exists), re-push the saved locationId so the select
    // reflects it. No-op in create mode (no saved locationId to sync).
    if (!locationsLoading && defaultValues?.locationId) {
      setValue('locationId', defaultValues.locationId);
    }
  }, [locationsLoading, defaultValues?.locationId, setValue]);

  async function handleFormSubmit(values: CreateEventRequest) {
    setServerError(null);
    try {
      await onSubmit(values);
    } catch (e) {
      setServerError(translateApiError(e, t, { fallback: t('eventForm.genericError') }));
    }
  }

  if (isFrozen) {
    return (
      <div className="rounded-sm border border-jq-sand/70 bg-jq-surface-2/50 px-4 py-3 text-[13px] text-jq-graphite">
        {effectiveStatus === 'closed' ? t('eventForm.frozenClosed') : t('eventForm.frozenActive')}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {serverError && <FormBanner message={serverError} />}

      <div className="grid gap-6 md:grid-cols-2">
        <Field label={t('eventForm.name.label')} htmlFor="name" error={errors.name?.message}>
          <Input
            id="name"
            placeholder={t('eventForm.name.placeholder')}
            disabled={isSubmitting || !editable.name}
            {...register('name')}
          />
        </Field>

        <Field
          label={t('eventForm.location')}
          htmlFor="locationId"
          error={errors.locationId?.message}
        >
          <SelectInput
            id="locationId"
            disabled={isSubmitting || !editable.locationId || locationsLoading}
            {...register('locationId')}
          >
            <option value="">
              {locationsLoading ? t('eventForm.loadingLocations') : t('eventForm.selectLocation')}
            </option>
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name}
              </option>
            ))}
          </SelectInput>
        </Field>

        <Field
          label={t('eventForm.date')}
          htmlFor="scheduledDate"
          error={errors.scheduledDate?.message}
        >
          <Input
            id="scheduledDate"
            type="date"
            disabled={isSubmitting || !editable.scheduledDate}
            {...register('scheduledDate')}
          />
        </Field>

        <Field
          label={t('eventForm.time')}
          htmlFor="scheduledTime"
          error={errors.scheduledTime?.message}
        >
          <Input
            id="scheduledTime"
            type="time"
            disabled={isSubmitting || !editable.scheduledTime}
            {...register('scheduledTime', {
              setValueAs: (v: string) => (v === '' ? null : v),
            })}
          />
        </Field>

        <Field
          label={t('eventForm.expectedCapacity.label')}
          htmlFor="expectedCapacity"
          error={errors.expectedCapacity?.message}
        >
          <Input
            id="expectedCapacity"
            type="number"
            min={1}
            placeholder={t('eventForm.expectedCapacity.placeholder')}
            disabled={isSubmitting || !editable.expectedCapacity}
            {...register('expectedCapacity', {
              setValueAs: (v: string) => (v === '' ? null : Number(v)),
            })}
          />
          <p className="mt-1 text-[12px] text-jq-graphite">{t('constants.expectedCapacityHint')}</p>
        </Field>

        <Field
          label={t('eventForm.chargeMode')}
          htmlFor="chargeMode"
          error={errors.chargeMode?.message}
        >
          <SelectInput
            id="chargeMode"
            disabled={isSubmitting || !editable.chargeMode}
            {...register('chargeMode')}
          >
            {CHARGE_MODE_OPTIONS.map((c) => (
              <option key={c.value} value={c.value}>
                {t(`constants.chargeMode.${c.value}`)}
              </option>
            ))}
          </SelectInput>
        </Field>

        <Field
          label={t('eventForm.photoPolicy')}
          htmlFor="photoPolicy"
          error={errors.photoPolicy?.message}
        >
          <SelectInput
            id="photoPolicy"
            disabled={isSubmitting || !editable.photoPolicy}
            {...register('photoPolicy')}
          >
            {EVENT_PHOTO_POLICY_OPTIONS.map((p) => (
              <option key={p.value} value={p.value}>
                {t(`constants.photoPolicy.${p.value}`)}
              </option>
            ))}
          </SelectInput>
          <p className="mt-1 text-[12px] text-jq-graphite">{t('constants.photoPolicyHint')}</p>
        </Field>
      </div>

      <div className="flex items-center justify-end gap-3 border-t border-jq-sand/70 pt-6">
        {onCancel && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            {t('common:actions.cancel')}
          </Button>
        )}
        <Button type="submit" size="sm" disabled={isSubmitting}>
          {isSubmitting ? t('eventForm.saving') : (submitLabel ?? t('eventForm.save'))}
        </Button>
      </div>
    </form>
  );
}

// ── Building blocks ────────────────────────────────────────────────────────

function Field({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label htmlFor={htmlFor} className="mb-1.5 block text-[13px] font-medium text-jq-charcoal">
        {label}
      </Label>
      {children}
      {error && (
        <p role="alert" className="mt-1.5 text-[12px] text-jq-danger">
          {error}
        </p>
      )}
    </div>
  );
}

const SelectInput = forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(
  function SelectInput({ className, ...rest }, ref) {
    return (
      <select
        ref={ref}
        {...rest}
        className={`flex h-10 w-full rounded-sm border border-jq-sand bg-jq-surface-0 px-3 text-[14px] text-jq-charcoal shadow-sm transition focus:border-jq-charcoal focus:outline-none focus:ring-1 focus:ring-jq-charcoal disabled:cursor-not-allowed disabled:opacity-50 ${
          className ?? ''
        }`}
      />
    );
  },
);

function FormBanner({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-4 py-3 text-[13px] text-jq-danger"
    >
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
    </div>
  );
}
